export const DirectPaymentMethod = {
  Card: "card",
  PayPal: "paypal",
  Venmo: "venmo",
  ApplePay: "applePay",
} as const;

export type DirectPaymentMethodValue =
  (typeof DirectPaymentMethod)[keyof typeof DirectPaymentMethod];

export const PaymentChoice = {
  ...DirectPaymentMethod,
  Echeck: "echeck",
} as const;

export type PaymentChoiceValue =
  (typeof PaymentChoice)[keyof typeof PaymentChoice];

export const AppLanguage = {
  English: "en-US",
  Spanish: "es-US",
} as const;

export type AppLanguageValue = (typeof AppLanguage)[keyof typeof AppLanguage];

export const HostedFieldSelector = {
  CardholderName: "#cardholder-name",
  Number: "#card-number",
  ExpirationDate: "#expiration-date",
  Cvv: "#cvv",
  PostalCode: "#postal-code",
} as const;

export const CardField = {
  CardholderName: "cardholderName",
  Number: "number",
  ExpirationDate: "expirationDate",
  Cvv: "cvv",
  PostalCode: "postalCode",
} as const;

export type CardFieldValue = (typeof CardField)[keyof typeof CardField];

export const CardFieldOrder: CardFieldValue[] = [
  CardField.CardholderName,
  CardField.Number,
  CardField.ExpirationDate,
  CardField.Cvv,
  CardField.PostalCode,
];

export const PaymentCancellationCode = {
  ApplePayCanceled: "APPLE_PAY_CANCELED",
  PayPalPopupClosed: "PAYPAL_POPUP_CLOSED",
  VenmoAppCanceled: "VENMO_APP_CANCELED",
  VenmoCanceled: "VENMO_CANCELED",
} as const;

export const PayPalButtonContainer = "#paypal-button";

export const BraintreePayloadType = {
  CreditCard: "CreditCard",
  PayPalAccount: "PayPalAccount",
  VenmoAccount: "VenmoAccount",
  ApplePayCard: "ApplePayCard",
} as const;

export const ApplePayMerchantDisplayName = "CMN Hospitals";

export const ApplePayDonationLabel = "Donation";

export const ApplePayVersion = 3;

/** Braintree expects its own locale format, not the app's language value. */
export const PayPalLocale: Record<AppLanguageValue, string> = {
  [AppLanguage.English]: "en_US",
  [AppLanguage.Spanish]: "es_ES",
};

/**
 * Hosted Fields render inside Braintree iframes, so their placeholders cannot
 * come from the translate pipe. Values are the ones the migration spec defines.
 */
export const HostedFieldPlaceholders: Record<
  AppLanguageValue,
  Record<CardFieldValue, string>
> = {
  [AppLanguage.English]: {
    cardholderName: "Cardholder name",
    number: ".... .... .... ....",
    expirationDate: "MM / YY",
    cvv: "CVV",
    postalCode: "Postal Code",
  },
  [AppLanguage.Spanish]: {
    cardholderName: "Nombre del titular",
    number: ".... .... .... ....",
    expirationDate: "MM / YY",
    cvv: "CVV",
    postalCode: "Código Postal",
  },
};

export function payPalLocaleFor(language: string): string {
  return (
    PayPalLocale[language as AppLanguageValue] ||
    PayPalLocale[AppLanguage.English]
  );
}

export function hostedFieldPlaceholdersFor(
  language: string
): Record<CardFieldValue, string> {
  return (
    HostedFieldPlaceholders[language as AppLanguageValue] ||
    HostedFieldPlaceholders[AppLanguage.English]
  );
}

/** Braintree rejects a zero total, so an unset amount falls back to one dollar. */
export function formatPaymentAmount(amount: number | string | null | undefined): string {
  const value = Number(amount);
  return (Number.isFinite(value) && value > 0 ? value : 1).toFixed(2);
}

function errorCode(error: unknown): unknown {
  if (!error || typeof error !== "object" || !("code" in error)) {
    return undefined;
  }

  return error.code;
}

export function isPaymentCancellation(error: unknown): boolean {
  const code = errorCode(error);
  return (
    code === PaymentCancellationCode.ApplePayCanceled ||
    code === PaymentCancellationCode.PayPalPopupClosed ||
    code === PaymentCancellationCode.VenmoAppCanceled ||
    code === PaymentCancellationCode.VenmoCanceled
  );
}
